import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { 
  getSubscriptionStatus, 
  createCheckoutSession, 
  createPortalSession,
  PLAN_CONFIGS,
  getPlanFeatures,
  calculatePlanPrice,
  formatPrice,
  getPlanDisplayName,
  getUpgradeRecommendation,
  type SubscriptionStatus
} from '../lib/billing'
import { CreditCard, Crown, Zap, Users, Check, ArrowUpRight } from 'lucide-react'
import { Button } from './ui/button'
import { Skeleton } from './ui/skeleton'

// ROLE AUDIT (2026-09-29, three-tier permission model -- see
// docs/ALERTING_PERMISSIONS_PLAN.md): explicit decision to leave this
// admin-only, not admin+manager. Billing/subscription/payment method is team
// governance, not pod-level oversight -- unlike agent_checkin/cost-per-user
// data (AgentsCostPanel), which managers do get scoped to their own pods.
// Revisit only if the product decision on manager scope changes.
interface BillingManagementProps {
  teamId: string
  userRole?: string
  isOpen?: boolean
  onClose?: () => void
  initialPlan?: string | null
}

export const BillingManagement: React.FC<BillingManagementProps> = ({ 
  teamId, 
  userRole = 'admin', 
  isOpen = false, 
  onClose, 
  initialPlan 
}) => {
  const { user } = useAuth()
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [upgrading, setUpgrading] = useState<string | null>(null)

  const isAdmin = userRole === 'admin' || userRole === 'owner'

  useEffect(() => {
    if (user && isAdmin) {
      loadSubscriptionStatus()
    } else {
      setLoading(false)
    }
  }, [user, teamId, isAdmin])

  // Poll for subscription updates after checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const billingStatus = urlParams.get('billing')
    
    if (billingStatus === 'success' && user && isAdmin) {
      // User returned from successful checkout, start polling
      console.log('🔄 User returned from checkout success, starting polling...')
      
      let pollCount = 0
      const maxPolls = 20 // Poll for up to 2 minutes
      
      const pollInterval = setInterval(async () => {
        pollCount++
        console.log(`🔄 Polling attempt ${pollCount}/${maxPolls}`)
        
        try {
          await loadSubscriptionStatus()
          
          // Check if subscription has been updated
          if (subscriptionStatus?.subscription?.status && 
              ['active', 'trialing'].includes(subscriptionStatus.subscription.status)) {
            console.log('✅ Subscription status updated!')
            clearInterval(pollInterval)
            // Clean up URL
            const newUrl = window.location.pathname + window.location.hash
            window.history.replaceState({}, '', newUrl)
          }
        } catch (error) {
          console.error('Error during polling:', error)
        }
        
        if (pollCount >= maxPolls) {
          clearInterval(pollInterval)
          console.log('⏰ Polling timeout reached')
        }
      }, 6000) // Poll every 6 seconds
      
      // Cleanup on unmount
      return () => clearInterval(pollInterval)
    }
  }, [user, isAdmin, teamId])

  // Auto-upgrade if initialPlan is provided
  useEffect(() => {
    if (initialPlan && subscriptionStatus && !upgrading) {
      handleUpgrade(initialPlan)
    }
  }, [initialPlan, subscriptionStatus, upgrading])

  const loadSubscriptionStatus = async () => {
    try {
      setLoading(true)
      setError('')
      const status = await getSubscriptionStatus(teamId, user!.id)
      setSubscriptionStatus(status)
    } catch (err: any) {
      console.error('Error loading subscription:', err)
      setError('Failed to load subscription details')
    } finally {
      setLoading(false)
    }
  }

  const handleUpgrade = async (planName: string) => {
    if (!user) return

    console.log('=== UPGRADE DEBUG ===')
    console.log('Team ID:', teamId)
    console.log('Plan Name:', planName)
    console.log('User ID:', user.id)
    console.log('User Email:', user.email)
    console.log('=====================')

    try {
      setUpgrading(planName)
      setError('')
      const checkoutUrl = await createCheckoutSession(teamId, planName, user.id)
      window.location.href = checkoutUrl
    } catch (err: any) {
      console.error('Error creating checkout:', err)
      setError('Failed to start upgrade process')
      setUpgrading(null)
    }
  }

  const handleManageBilling = async () => {
    if (!user) return

    console.log('=== BILLING DEBUG ===')
    console.log('Team ID:', teamId)
    console.log('User ID:', user.id)
    console.log('User Email:', user.email)
    console.log('=====================')

    try {
      setError('')
      const portalUrl = await createPortalSession(teamId, user.id)
      window.open(portalUrl, '_blank')
    } catch (err: any) {
      console.error('Error opening billing portal:', err)
      
      if (err.name === 'SubscriptionRequiredError' || err.needsSubscription) {
        setError(`You need to subscribe to a plan first to access billing management. Your current plan is ${getPlanDisplayName(err.teamPlan || currentPlan)}. Please choose a plan below to get started.`)
      } else if (err.message?.includes('No configuration provided')) {
        setError('Billing portal is being set up. Please use the subscription options below to manage your plan.')
      } else {
        setError(err.message || 'Failed to open billing portal')
      }
    }
  }

  const handleCancelSubscription = async () => {
    if (!user || !confirm('Are you sure you want to cancel your subscription? You will lose access to paid features at the end of your billing period.')) return

    try {
      setError('')
      setUpgrading('cancel')
      const portalUrl = await createPortalSession(teamId, user.id)
      window.open(portalUrl, '_blank')
    } catch (err: any) {
      console.error('Error opening billing portal:', err)
      if (err.message?.includes('No configuration provided')) {
        setError('Billing portal is being set up. Please contact support to cancel your subscription.')
      } else {
        setError(err.message || 'Failed to open billing portal')
      }
    } finally {
      setUpgrading(null)
    }
  }

  if (!isAdmin) {
    // No border-radius (§1); neutral bg-muted, not a yellow warning box --
    // this is ordinary role gating, not an alert, matching the plain-text
    // "Only team admins can..." convention used by ScheduleConfiguration /
    // DeliveryConfiguration / ManagersConfiguration.
    return (
      <div className="border border-border bg-muted p-4">
        <div className="text-sm text-muted-foreground">
          Only team administrators can manage billing settings.
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Current Plan card shape. No border-radius on the panel (§1); the
            small plan-icon badge keeps its radius (icon chip, not a panel). */}
        <div className="bg-background border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-28" />
          </div>
          <div className="flex items-center space-x-3 mb-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
          <div className="bg-muted p-4 mb-4 space-y-2">
            <Skeleton className="h-3 w-48" />
            <Skeleton className="h-3 w-40" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-56" />
            <Skeleton className="h-3 w-52" />
            <Skeleton className="h-3 w-48" />
          </div>
        </div>

        {/* Plan option cards shape. No border-radius (§1). */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[0, 1].map((i) => (
            <div key={i} className="border border-border p-6 space-y-4">
              <div className="flex items-center space-x-3">
                <Skeleton className="h-9 w-9 rounded-lg" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
              <div className="space-y-2">
                <Skeleton className="h-3 w-40" />
                <Skeleton className="h-3 w-36" />
              </div>
              <Skeleton className="h-9 w-full" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const currentPlan = subscriptionStatus?.subscription?.plan_name || subscriptionStatus?.team_plan || 'trial'
  
  // Debug logging
  console.log('=== BILLING DEBUG ===')
  console.log('Full Subscription Status:', JSON.stringify(subscriptionStatus, null, 2))
  console.log('Subscription object:', subscriptionStatus?.subscription)
  console.log('Team plan fallback:', subscriptionStatus?.team_plan)
  console.log('Calculated current plan:', currentPlan)
  console.log('Subscription status:', subscriptionStatus?.subscription?.status)
  console.log('Plan name from subscription:', subscriptionStatus?.subscription?.plan_name)
  console.log('=== END DEBUG ===')  
  
  const currentFeatures = getPlanFeatures(currentPlan)
  const memberCount = subscriptionStatus?.member_count || 0
  const recommendedPlan = getUpgradeRecommendation(currentPlan, memberCount)
  const hasActiveSubscription = subscriptionStatus?.subscription?.status === 'active'

  const content = (
    <div className="space-y-6">
      {error && (
        <div className="border border-destructive/30 bg-destructive/10 p-4">
          <div className="text-sm text-destructive">{error}</div>
        </div>
      )}

      {/* Current Plan Status. No border-radius on the panel (§1). */}
      <div className="bg-background border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Current Plan</h3>
          {/* Show manage billing button - error handling will guide users to subscribe if needed */}
          <Button
            variant="link"
            onClick={handleManageBilling}
            className="h-auto p-0 gap-2 text-sm"
          >
            <CreditCard className="h-4 w-4" />
            <span>Manage Billing</span>
            <ArrowUpRight className="!size-3" />
          </Button>
        </div>

        <div className="flex items-center space-x-3 mb-4">
          <div className={`p-2 rounded-lg ${
            currentPlan === 'trial' ? 'bg-gray-100' :
            currentPlan === 'advanced_trial' ? 'bg-purple-100' :
            currentPlan === 'essentials' ? 'bg-blue-100' :
            'bg-purple-100'
          }`}>
            {currentPlan === 'trial' ? <Users className="h-5 w-5 text-gray-600" /> :
             currentPlan === 'advanced_trial' ? <Zap className="h-5 w-5 text-purple-600" /> :
             currentPlan === 'essentials' ? <Crown className="h-5 w-5 text-blue-600" /> :
             <Zap className="h-5 w-5 text-purple-600" />}
          </div>
          <div>
            <h4 className="font-medium text-foreground">
              {getPlanDisplayName(currentPlan)}
              {(subscriptionStatus?.subscription?.status === 'trialing' ||
                (subscriptionStatus?.subscription?.status === 'active' && subscriptionStatus?.subscription?.current_period_end && new Date(subscriptionStatus?.subscription?.current_period_end) > new Date()) ||
                ['trial', 'advanced_trial'].includes(subscriptionStatus?.team_plan || ''))
                && ' (Free Trial)'}
            </h4>
            <p className="text-sm text-muted-foreground">
              {memberCount} of {
                typeof currentFeatures.maxUsers === 'number' && currentFeatures.maxUsers !== Infinity
                  ? currentFeatures.maxUsers
                  : '∞'
              } users
            </p>
          </div>
        </div>

        {(subscriptionStatus?.subscription || ['trial', 'advanced_trial'].includes(subscriptionStatus?.team_plan || '')) && (
          <div className="bg-muted p-4 mb-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Status:</span>
                {/* text-emerald-600/text-amber-600 (not the semantic --success
                    token, which renders amber, or a raw green/yellow) --
                    matches the app's established exception for status colors
                    that need to read unambiguously green vs. not, per
                    UI_STYLE_GUIDE.md §7. */}
                <span className={`ml-2 font-medium ${
                  (subscriptionStatus?.subscription?.status === 'active' ||
                   subscriptionStatus?.subscription?.status === 'trialing' ||
                   ['trial', 'advanced_trial'].includes(subscriptionStatus?.team_plan || '')) ? 'text-emerald-600' : 'text-amber-600'
                }`}>
                  {subscriptionStatus?.subscription?.status === 'active' && subscriptionStatus?.subscription?.current_period_end && new Date(subscriptionStatus?.subscription?.current_period_end) > new Date()
                    ? 'trialing'
                    : subscriptionStatus?.subscription?.status || (['trial', 'advanced_trial'].includes(subscriptionStatus?.team_plan || '') ? 'trialing' : 'inactive')}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">
                  {(subscriptionStatus?.subscription?.status === 'trialing' ||
                    (subscriptionStatus?.subscription?.status === 'active' && subscriptionStatus?.subscription?.current_period_end && new Date(subscriptionStatus?.subscription?.current_period_end) > new Date()) ||
                    ['trial', 'advanced_trial'].includes(subscriptionStatus?.team_plan || ''))
                    ? 'Trial ends:' : 'Next billing:'}
                </span>
                <span className="ml-2 font-medium text-foreground">
                  {subscriptionStatus?.subscription?.current_period_end
                    ? new Date(subscriptionStatus?.subscription?.current_period_end).toLocaleDateString()
                    : (subscriptionStatus?.team_created_at && ['trial', 'advanced_trial'].includes(subscriptionStatus?.team_plan || ''))
                      ? (() => {
                          const trialEnd = new Date(subscriptionStatus.team_created_at)
                          trialEnd.setDate(trialEnd.getDate() + 7)
                          return trialEnd.toLocaleDateString()
                        })()
                      : 'Date not set'}
                </span>
              </div>
            </div>
            {subscriptionStatus?.subscription?.cancel_at_period_end && (
              <div className="mt-2 text-sm text-amber-600">
                Your subscription will cancel at the end of the current period.
              </div>
            )}
          </div>
        )}

        {/* Cancel Subscription Button - only show for active subscriptions */}
        {hasActiveSubscription && !subscriptionStatus?.subscription?.cancel_at_period_end && (
          <div className="flex justify-end">
            <Button
              variant="link"
              onClick={handleCancelSubscription}
              disabled={upgrading === 'cancel'}
              className="h-auto p-0 text-sm font-medium text-destructive hover:text-destructive/90"
            >
              {upgrading === 'cancel' ? 'Processing...' : 'Cancel Subscription'}
            </Button>
          </div>
        )}

        {/* Current Plan Features. text-emerald-500 checkmarks, not raw
            green-500 or the semantic --success token (renders amber) --
            matches the app's established emerald-for-"included"/"active"
            exception, per UI_STYLE_GUIDE.md §7. */}
        <div className="space-y-2">
          <h5 className="font-medium text-foreground">Current Features</h5>
          <div className="grid grid-cols-1 gap-2">
            <div className="flex items-center space-x-2 text-sm">
              <Check className="h-4 w-4 text-emerald-500" />
              <span>
                Up to {
                  typeof currentFeatures.maxUsers === 'number'
                    ? currentFeatures.maxUsers
                    : 'unlimited'
                } team members
              </span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              {currentFeatures.dailySummaries ? (
                <Check className="h-4 w-4 text-emerald-500" />
              ) : (
                <span className="h-4 w-4 rounded-full border-2 border-border" />
              )}
              <span className={currentFeatures.dailySummaries ? '' : 'text-muted-foreground'}>
                AI-powered daily summaries
              </span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              {currentFeatures.advancedAnalytics ? (
                <Check className="h-4 w-4 text-emerald-500" />
              ) : (
                <span className="h-4 w-4 rounded-full border-2 border-border" />
              )}
              <span className={currentFeatures.advancedAnalytics ? '' : 'text-muted-foreground'}>
                Advanced analytics & insights
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Recommendation. amber-* kept (not swapped to a semantic
          token) -- this is exactly the "pending/recommendation" case
          UI_STYLE_GUIDE.md §7 names as the accepted exception. No
          border-radius on the panel (§1). */}
      {recommendedPlan && (
        <div className="border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Crown className="h-5 w-5 text-amber-600 mt-0.5" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-amber-800">Upgrade Recommended</h4>
              <p className="text-sm text-amber-700 mt-1">
                You have {memberCount} team members. Consider upgrading to {getPlanDisplayName(recommendedPlan)}
                for {recommendedPlan === 'essentials' ? 'up to 4 users' : 'unlimited users'} and additional features.
              </p>
              <Button
                onClick={() => handleUpgrade(recommendedPlan)}
                disabled={upgrading === recommendedPlan}
                size="sm"
                className="mt-2 text-xs"
              >
                {upgrading === recommendedPlan ? 'Processing...' : `Upgrade to ${getPlanDisplayName(recommendedPlan)}`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Options - only show if on trial or can upgrade to different plan */}
      {(currentPlan === 'trial' || currentPlan === 'advanced_trial' || !hasActiveSubscription) && (
        <>
          {(currentPlan === 'trial' || currentPlan === 'advanced_trial') && (
            <div className="mb-4 text-center">
              <h3 className="text-lg font-semibold text-foreground mb-2">Choose Your Plan</h3>
              <p className="text-sm text-muted-foreground">
                You're currently on a free trial. Select a plan to continue after your trial ends.
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(PLAN_CONFIGS)
            .filter(([name]) => !['trial', 'advanced_trial'].includes(name) && name !== currentPlan)
            .map(([planName, features]) => (
            // No border-radius on the panel (§1); the plan-icon badge below
            // keeps rounded-lg (icon chip, exempt) and its bg-blue-100/
            // bg-purple-100 per-plan colors, matching the same accepted
            // categorical-swatch convention as POD_COLORS/PROJECT_AVATAR_PALETTE
            // elsewhere in the app.
            <div key={planName} className="border border-border p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className={`p-2 rounded-lg ${
                  planName === 'essentials' ? 'bg-blue-100' : 'bg-purple-100'
                }`}>
                  {planName === 'essentials' ?
                    <Crown className="h-5 w-5 text-blue-600" /> :
                    <Zap className="h-5 w-5 text-purple-600" />
                  }
                </div>
                <div>
                  <h4 className="font-semibold text-foreground">{getPlanDisplayName(planName)}</h4>
                  <p className="text-sm text-muted-foreground">
                    {formatPrice(features.price)}{features.pricePerUser ? '/user' : ''}/month
                    {features.pricePerUser && memberCount > 1 && (
                      <span className="text-xs text-muted-foreground/70 block">
                        {formatPrice(calculatePlanPrice(planName, memberCount))} total for {memberCount} users
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className="space-y-2 mb-6">
                <div className="flex items-center space-x-2 text-sm">
                  <Check className="h-4 w-4 text-emerald-500" />
                  <span>
                    Up to {
                      typeof features.maxUsers === 'number'
                        ? features.maxUsers
                        : 'unlimited'
                    } team members
                  </span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <Check className="h-4 w-4 text-emerald-500" />
                  <span>AI-powered daily summaries</span>
                </div>
                {features.advancedAnalytics && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Check className="h-4 w-4 text-emerald-500" />
                    <span>Advanced analytics & insights</span>
                  </div>
                )}
              </div>

              <Button
                onClick={() => handleUpgrade(planName)}
                disabled={upgrading === planName}
                className="w-full"
              >
                {upgrading === planName ? 'Processing...' :
                  (currentPlan === 'trial' || currentPlan === 'advanced_trial')
                    ? `Upgrade to ${getPlanDisplayName(planName)}`
                    : `Start ${getPlanDisplayName(planName)} Trial`
                }
              </Button>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  )

  // Return modal version if isOpen is true
  if (isOpen) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        {/* Dialog surface keeps its radius -- §1 exempts dialogs/popovers from
            the no-radius rule, unlike a card/panel/table. */}
        <div className="bg-background rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-foreground">Billing & Subscription</h2>
            {onClose && (
              <Button
                onClick={onClose}
                aria-label="close"
                variant="ghost"
                size="icon"
                className="size-11 text-muted-foreground hover:text-foreground"
              >
                <svg className="!size-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            )}
          </div>
          {content}
        </div>
      </div>
    )
  }

  return content
}
